import * as React from 'react';

import { useForm } from 'react-hook-form';

import {
  LabelSize,
  LabelData,
  LabelMargins,
  BarcodeDisplayOptions,
  BarcodeTypes,
  LabelContent,
} from 'label/utils/types';
import { getOptions } from 'pages/views/devices/utils/options';
import Format from 'root/apps/app/src/data/Format';
import { getItemPrintedTimestamp } from 'root/apps/label/utils/timeStampHelpers';

import { getLabelQuantity } from '../HubLabelPrintModal';

import { useLocalStorage } from '.';

interface FieldArray {
  name:
    | 'fieldArray'
    | 'quantity.labels'
    | 'default'
    | 'labelMargins.small'
    | 'labelMargins.normal'
    | 'labelMargins.alt'
    | 'labelMargins.jewelry';
  value: number;
  id: number;
}

export function useLabelPrintForm({
  labelData,
  isLoading,
}: {
  labelData: LabelData[] | undefined;
  isLoading: boolean;
}) {
  const [previousLabelSize, setPreviousLabelSize] = useLocalStorage<LabelSize | undefined>(
    'LABEL_SIZE',
    undefined
  );

  const [previousLabelMarginsAlt, setPreviousLabelMarginAlt] = useLocalStorage(
    'LABEL_MARGIN_ALT',
    0
  );
  const [previousLabelMarginsJewelry, setPreviousLabelMarginJewelry] = useLocalStorage(
    'LABEL_MARGIN_JEWELRY',
    0
  );
  const [previousLabelMarginsSmall, setPreviousLabelMarginSmall] = useLocalStorage(
    'LABEL_MARGIN_SMALL',
    0
  );
  const [previousLabelMarginsNormal, setPreviousLabelMarginNormal] = useLocalStorage(
    'LABEL_MARGIN_NORMAL',
    0
  );

  const [previousDisplayPrice, setPreviousDisplayPrice] = useLocalStorage(
    'LABEL_DISPLAY_PRICE',
    true
  );

  const [previousLabelContent, setPreviousLabelContent] = useLocalStorage<LabelContent | null>(
    'LABEL_CONTENT',
    null
  );

  const [
    previousLabelContentDefault,
    setPreviousLabelContentDefault,
  ] = useLocalStorage<LabelContent | null>('LABEL_CONTENT_DEFAULT', null);

  const [dateNow, setDateNow] = React.useState<string | undefined>();

  const [previousBarcodeDisplayOption, setPreviousBarcodeDisplayOption] = useLocalStorage(
    'LABEL_BARCODE_DISPLAY_OPTION',
    BarcodeDisplayOptions.BARCODE_AND_NUMBERS
  );

  const [previousBarcodeType, setPreviousBarcodeType] = useLocalStorage(
    'LABEL_BARCODE_TYPE',
    BarcodeTypes.SYSTEM_SKU
  );

  const setPreviousLabelMargin = (labelMargins: LabelMargins) => {
    setPreviousLabelMarginAlt(labelMargins.alt);
    setPreviousLabelMarginJewelry(labelMargins.jewelry);
    setPreviousLabelMarginNormal(labelMargins.normal);
    setPreviousLabelMarginSmall(labelMargins.small);
  };

  const { control, watch, register, setValue, formState, handleSubmit, getValues } = useForm<{
    fieldArray: FieldArray[];
    labelQuantity: number;
    quantity: { labels: number };
    labelSize: LabelSize;
    labelMargins: LabelMargins;
    default: undefined;
    displayPrice: boolean;
    barcodeDisplayOption: BarcodeDisplayOptions;
    barcodeType: BarcodeTypes;
    labelContent: LabelContent | null;
    labelContentDefault: LabelContent | null;
  }>({
    defaultValues: {
      labelQuantity: 1,
      quantity: {
        labels: 1,
      },
      labelSize: previousLabelSize,
      labelMargins: {
        alt: previousLabelMarginsAlt,
        jewelry: previousLabelMarginsJewelry,
        normal: previousLabelMarginsNormal,
        small: previousLabelMarginsSmall,
      },
      displayPrice: previousDisplayPrice,
      barcodeDisplayOption: previousBarcodeDisplayOption,
      barcodeType: previousBarcodeType,
      labelContent: previousLabelContent,
      labelContentDefault: previousLabelContentDefault,
      fieldArray: [
        { name: 'default', value: 0, id: 1 },
        { name: 'quantity.labels', value: 1, id: 2 },
        { name: 'labelMargins.jewelry', value: 1, id: 3 },
        { name: 'labelMargins.small', value: 1, id: 4 },
        { name: 'labelMargins.normal', value: 1, id: 5 },
        { name: 'labelMargins.alt', value: 1, id: 6 },
      ],
    },
    mode: 'onChange',
  });

  const {
    labelQuantity,
    labelSize,
    labelMargins,
    fieldArray,
    quantity,
    displayPrice,
    barcodeDisplayOption,
    barcodeType,
    labelContent,
    labelContentDefault,
  } = watch();

  const setLabelContentDefault = (value: LabelContent) => {
    setValue('labelContentDefault', value);
    setPreviousLabelContentDefault(value);
  };

  const getIsMsrpSet = async () => {
    const options = getOptions();
    const { accountId, shopId } = options;

    const res = await fetch(`/API/Account/${accountId}/Shop/${shopId}.json`)
      .then(response => {
        return response.json();
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error retrieving shop data', err);
      });

    const {
      Shop: { labelMsrp },
    } = res;

    const labelMsrpAccountLevelSetting = Format.parseBoolean(labelMsrp, false);

    labelMsrpAccountLevelSetting
      ? setLabelContentDefault(LabelContent.MSRP)
      : setLabelContentDefault(LabelContent.NONE);

    if (labelContent === null && labelMsrpAccountLevelSetting) {
      setValue('labelContent', LabelContent.MSRP);
      setPreviousLabelContent(LabelContent.MSRP);
    }

    if (labelContent === null && !labelMsrpAccountLevelSetting) {
      setValue('labelContent', LabelContent.NONE);
      setPreviousLabelContent(LabelContent.NONE);
    }

    return labelMsrpAccountLevelSetting;
  };

  React.useEffect(() => {
    getIsMsrpSet();

    const updateTimestamp = async () => {
      setDateNow(await getItemPrintedTimestamp());
    };

    updateTimestamp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setValue('quantity.labels', getLabelQuantity(labelData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelData]);

  React.useEffect(() => {
    setValue('labelQuantity', quantity.labels || 1);
  }, [quantity.labels, setValue]);

  React.useEffect(() => {
    setValue('fieldArray', fieldArray);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelData, isLoading, setValue]);

  return {
    control,
    displayPrice,
    setPreviousDisplayPrice,
    barcodeDisplayOption,
    previousBarcodeDisplayOption,
    setPreviousBarcodeDisplayOption,
    barcodeType,
    setPreviousBarcodeType,
    labelContent,
    labelContentDefault,
    setPreviousLabelContent,
    dateNow,
    labelQuantity,
    quantity,
    labelSize,
    getValues,
    previousLabelSize,
    setPreviousLabelSize,
    labelMargins,
    formState,
    setValue,
    register,
    setPreviousLabelMargin,
    handleSubmit,
    watch,
    fieldArray,
  };
}
